
import React, { useState, useEffect, useRef } from 'react';
import { X, Building, Check, AlertCircle, MapPin, Globe, User, Calendar, Edit3, Save } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const DispensaryDetailModal = ({ dispensary, onClose, onApprove, onReject, actionLoading }) => {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const { colors } = useTheme(); 
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (dispensary) {
      setEditData({
        name: dispensary.name || '',
        address: dispensary.address || '',
        city: dispensary.city || '',
        state: dispensary.state || '',
        postal_code: dispensary.postal_code || '',
        latitude: dispensary.latitude || '',
        longitude: dispensary.longitude || '',
        website: dispensary.website || ''
      });
    }
  }, [dispensary]);

  // Load Google Maps API
  useEffect(() => {
    if (!window.google && !window.googleMapsLoading) {
      window.googleMapsLoading = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsMapLoaded(true);
        window.googleMapsLoading = false;
      };
      document.head.appendChild(script);
    } else if (window.google) {
      setIsMapLoaded(true);
    }
  }, []);

  // Initialize map when dispensary data is available and Google Maps is loaded
  useEffect(() => {
    if (dispensary && isMapLoaded && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [dispensary, isMapLoaded]);

  const initializeMap = () => {
    if (!window.google || !mapRef.current) return;

    const lat = parseFloat(dispensary.latitude) || 37.7749;
    const lng = parseFloat(dispensary.longitude) || -122.4194;
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: dispensary.latitude && dispensary.longitude ? 16 : 10,
      center: { lat, lng },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Add marker if coordinates exist
    if (dispensary.latitude && dispensary.longitude) {
      updateMapMarker(lat, lng);
    }

    // Add click listener to map when in edit mode
    mapInstanceRef.current.addListener('click', (event) => {
      if (editMode) {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        updateMapMarker(newLat, newLng);
        setEditData(prev => ({
          ...prev,
          latitude: newLat.toString(),
          longitude: newLng.toString()
        }));
      }
    });
  };

  const updateMapMarker = (lat, lng) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      draggable: editMode,
      title: dispensary.name || 'Dispensary Location'
    });

    // Update coordinates when marker is dragged (only in edit mode)
    if (editMode) {
      markerRef.current.addListener('dragend', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        setEditData(prev => ({
          ...prev,
          latitude: newLat.toString(),
          longitude: newLng.toString()
        }));
      });
    }
  };

  // Update map when edit mode changes or coordinates change
  useEffect(() => {
    if (mapInstanceRef.current && editData.latitude && editData.longitude) {
      const lat = parseFloat(editData.latitude);
      const lng = parseFloat(editData.longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        updateMapMarker(lat, lng);
        mapInstanceRef.current.setCenter({ lat, lng });
      }
    }
  }, [editMode, editData.latitude, editData.longitude]);

  if (!dispensary) return null;

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleApprove = () => {
    if (editMode) {
      // Approve with edited data
      onApprove(dispensary.id, editData);
    } else {
      // Approve as-is
      onApprove(dispensary.id);
    }
  };

  const handleReject = () => {
    onReject(dispensary.id, rejectReason);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const hasCoordinates = dispensary.latitude && dispensary.longitude;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '20px',
        width: '100%',
        maxWidth: hasCoordinates ? '1200px' : '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building style={{ color: '#00d4aa' }} size={24} />
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Review Dispensary
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                color: '#888',
                fontSize: '14px'
              }}>
                {editMode ? 'Edit and approve' : 'Review submission details'}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setEditMode(!editMode)}
              style={{
                padding: '8px 16px',
                background: editMode ? '#666' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              {editMode ? <Save size={16} /> : <Edit3 size={16} />}
              {editMode ? 'View Mode' : 'Edit Mode'}
            </button>

            <button
              onClick={onClose}
              disabled={actionLoading}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!actionLoading) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (!actionLoading) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#888';
                }
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          display: hasCoordinates ? 'grid' : 'block',
          gridTemplateColumns: hasCoordinates ? '1fr 400px' : '1fr',
          height: 'calc(90vh - 200px)',
          overflow: 'hidden'
        }}>
          {/* Main Content */}
          <div style={{
            padding: '24px',
            overflowY: 'auto'
          }}>
            {/* Submission Meta Info */}
            <div style={{
              background: '#2a2a2a',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #333'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#e5e5e5', fontSize: '18px', fontWeight: '600' }}>
                Submission Details
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={16} style={{ color: '#8b5cf6' }} />
                  <span style={{ color: '#b3b3b3', fontSize: '14px' }}>
                    Submitted by: <strong>{dispensary.submitted_by_username}</strong>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={16} style={{ color: '#888' }} />
                  <span style={{ color: '#b3b3b3', fontSize: '14px' }}>
                    {new Date(dispensary.created_at).toLocaleString()}
                  </span>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  STATUS: PENDING REVIEW
                </div>
              </div>
            </div>

            {/* Dispensary Information */}
            <div style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #333'
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: '#e5e5e5', fontSize: '18px', fontWeight: '600' }}>
                Dispensary Information
              </h3>

              <div style={{ display: 'grid', gap: '20px' }}>
                {/* Name */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}>
                    Dispensary Name *
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        border: '2px solid #333',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
                    />
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      {dispensary.name}
                    </div>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}>
                    Street Address
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        border: '2px solid #333',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      {dispensary.address || 'Not provided'}
                    </div>
                  )}
                </div>

                {/* City, State, ZIP */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      City
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        {dispensary.city || 'Not provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      State
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        maxLength="2"
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        {dispensary.state || 'Not provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      ZIP Code
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        {dispensary.postal_code || 'Not provided'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Website */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}>
                    Website
                  </label>
                  {editMode ? (
                    <input
                      type="url"
                      value={editData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://example.com"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        border: '2px solid #333',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  ) : (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      {dispensary.website ? (
                        <a href={dispensary.website} target="_blank" rel="noopener noreferrer" 
                           style={{ color: '#00d4aa', textDecoration: 'none' }}>
                          {dispensary.website}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  )}
                </div>

                {/* Coordinates */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      Latitude
                    </label>
                    {editMode ? (
                      <input
                        type="number"
                        step="any"
                        value={editData.latitude}
                        onChange={(e) => handleInputChange('latitude', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        {dispensary.latitude || 'Not provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}>
                      Longitude
                    </label>
                    {editMode ? (
                      <input
                        type="number"
                        step="any"
                        value={editData.longitude}
                        onChange={(e) => handleInputChange('longitude', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px'
                      }}>
                        {dispensary.longitude || 'Not provided'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Reject Form */}
            {showRejectForm && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '20px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#ef4444', fontSize: '16px', fontWeight: '600' }}>
                  Reject Dispensary Submission
                </h4>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Provide a reason for rejection (optional)..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={handleReject}
                    disabled={actionLoading}
                    style={{
                      padding: '10px 20px',
                      background: actionLoading ? '#666' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: actionLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Confirm Rejection
                  </button>
                  <button
                    onClick={() => setShowRejectForm(false)}
                    style={{
                      padding: '10px 20px',
                      background: '#333',
                      color: '#e5e5e5',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Map Panel */}
          {hasCoordinates && (
            <div style={{
              borderLeft: '1px solid #333',
              background: '#2a2a2a',
              position: 'relative'
            }}>
              <div style={{
                padding: '16px',
                borderBottom: `1px solid ${colors.border}`,
                background: '#1a1a1a'
              }}>
                <h4 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MapPin size={16} style={{ color: '#00d4aa' }} />
                  Location Verification
                </h4>
                {editMode && (
                  <p style={{
                    margin: '4px 0 0 24px',
                    fontSize: '12px',
                    color: '#888'
                  }}>
                    Click on map or drag marker to update location
                  </p>
                )}
              </div>
              
              {!isMapLoaded ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 80px)',
                  color: '#888'
                }}>
                  Loading map...
                </div>
              ) : (
                <div
                  ref={mapRef}
                  style={{
                    width: '100%',
                    height: 'calc(100% - 80px)'
                  }}
                />
              )}
              
              <div style={{
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                right: '12px',
                background: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {dispensary.latitude}, {dispensary.longitude}
              </div>
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div style={{
          padding: '24px',
          borderTop: '1px solid #333',
          background: colors.bgTertiary,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={() => setShowRejectForm(true)}
            disabled={actionLoading || showRejectForm}
            style={{
              padding: '12px 24px',
              background: (actionLoading || showRejectForm) ? '#666' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: (actionLoading || showRejectForm) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <AlertCircle size={16} />
            Reject
          </button>

          <button
            onClick={handleApprove}
            disabled={actionLoading}
            style={{
              padding: '12px 24px',
              background: actionLoading ? '#666' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: actionLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {actionLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ccc',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Processing...
              </>
            ) : (
              <>
                <Check size={16} />
                {editMode ? 'Approve with Changes' : 'Approve as Submitted'}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};